/**
 * Botanique theme script. No libraries; native custom elements and <dialog>.
 * Budget: total theme JS under 60 KB gzipped (spec section 9).
 */
(() => {
  const config = window.Botanique || { routes: {}, strings: {} };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------- Helpers ---------- */

  const announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => (region.textContent = message), 50);
  };

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const publish = (name, detail) => {
    try {
      window.Shopify?.analytics?.publish?.(name, detail);
    } catch (e) {
      /* analytics is optional */
    }
  };
  window.Botanique.publish = publish;

  const format = (template, values) =>
    Object.entries(values).reduce((out, [k, v]) => out.replaceAll(`[${k}]`, v), template || '');

  /* ---------- Dialogs: open, close, backdrop click, focus return ---------- */

  const openers = new WeakMap();

  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener || document.activeElement);
    dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('[data-open-dialog]');
    if (openBtn) {
      openDialog(document.getElementById(openBtn.dataset.openDialog), openBtn);
      return;
    }
    const closeBtn = event.target.closest('[data-close-dialog]');
    if (closeBtn) {
      closeBtn.closest('dialog')?.close();
      return;
    }
    // A click on the backdrop lands on the dialog element itself.
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      const dialog = event.target;
      let opener = openers.get(dialog);
      // The opener may have been re-rendered while the dialog was open.
      if (!opener || !document.contains(opener)) opener = dialog.id && document.querySelector(`[aria-controls="${dialog.id}"]`);
      if (opener) opener.focus();
    },
    true,
  );

  /* ---------- Disclosure buttons (menu drawer) ---------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-disclosure]');
    if (!btn) return;
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    if (panel) panel.hidden = open;
  });

  /* ---------- Mobile search toggle ---------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-toggle-search]');
    if (!btn) return;
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    panel?.classList.toggle('is-open', !open);
    if (!open) panel?.querySelector('input[type="search"]')?.focus();
  });

  /* ---------- Sticky header: publish its height for scroll-padding ---------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.observer = new ResizeObserver(() => {
        const height = this.dataset.sticky === 'true' ? this.offsetHeight : 0;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Mega menu: disclosure buttons, click and hover intent ---------- */

  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('button[aria-controls]')];
      this.openTimer = null;
      this.closeTimer = null;

      this.buttons.forEach((button) => {
        const item = button.closest('[data-menu-item]');
        button.addEventListener('click', () => this.toggle(button));
        item.addEventListener('pointerenter', () => {
          if (!finePointer.matches) return;
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => this.open(button), 150);
        });
        item.addEventListener('pointerleave', () => {
          if (!finePointer.matches) return;
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.close(button), 200);
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.close(button);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.buttons.find((b) => b.getAttribute('aria-expanded') === 'true');
        if (open) {
          this.close(open);
          open.focus();
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.buttons.forEach((b) => this.close(b));
      });
    }

    toggle(button) {
      button.getAttribute('aria-expanded') === 'true' ? this.close(button) : this.open(button);
    }

    open(button) {
      this.buttons.forEach((b) => b !== button && this.close(b));
      button.setAttribute('aria-expanded', 'true');
      document.getElementById(button.getAttribute('aria-controls')).hidden = false;
    }

    close(button) {
      button.setAttribute('aria-expanded', 'false');
      document.getElementById(button.getAttribute('aria-controls')).hidden = true;
    }
  }
  customElements.define('mega-menu', MegaMenu);

  /* ---------- Predictive search: combobox with listbox results ---------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[id^="SearchResults"]');
      this.controller = null;
      this.input.addEventListener('input', debounce(() => this.search(), 200));
      this.input.addEventListener('keydown', (event) => this.onKey(event));
      this.input.addEventListener('focus', () => this.input.value.trim() && this.results.innerHTML && this.show());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hide();
      });
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) return this.hide();
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: this.dataset.sectionId,
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.sku,variants.title,tag',
      });
      try {
        const res = await fetch(`${config.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('.predictive');
        this.results.innerHTML = content ? content.outerHTML : '';
        this.options = [...this.results.querySelectorAll('[role="option"]')];
        this.active = -1;
        this.show();
        const count = Number(content?.dataset.resultsCount || 0);
        const message = count === 1 ? config.strings.searchResultsOne : format(config.strings.searchResults, { count });
        announce(count ? message : this.results.textContent.trim());
      } catch (e) {
        if (e.name !== 'AbortError') this.hide();
      }
    }

    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    onKey(event) {
      if (event.key === 'Escape') {
        this.hide();
        return;
      }
      if (!this.options?.length || this.results.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.active = (this.active + step + this.options.length) % this.options.length;
        this.options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.active)));
        const current = this.options[this.active];
        this.input.setAttribute('aria-activedescendant', current.id);
        current.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault();
        const link = this.options[this.active].querySelector('a');
        if (link) window.location.href = link.href;
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Quantity stepper ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('[data-step]').forEach((button) =>
        button.addEventListener('click', () => {
          const min = Number(this.input.min || 0);
          const next = Math.max(min, (Number(this.input.value) || 0) + Number(button.dataset.step));
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }),
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart API ---------- */

  const cart = {
    sectionId: 'cart-drawer',
    extraSections: [],

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: [this.sectionId, ...this.extraSections], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || config.strings.addError);
      return data;
    },

    render(sections) {
      if (sections) document.dispatchEvent(new CustomEvent('cart:sections', { detail: sections }));
      const html = sections?.[this.sectionId];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.getElementById('CartDrawerContent');
      const current = document.getElementById('CartDrawerContent');
      if (!next || !current) return;
      current.replaceWith(next);
      const count = Number(next.dataset.itemCount || 0);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },

    add(items) {
      return this.request(config.routes.cartAdd, { items });
    },

    change(line, quantity) {
      return this.request(config.routes.cartChange, { line, quantity });
    },
  };
  window.Botanique.cart = cart;

  /* ---------- Product forms (cards, product page, quick view) ---------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('[data-variant-input]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.querySelectorAll('[data-unit-option]').forEach((radio) =>
        radio.addEventListener('change', () => (this.idInput.value = radio.value)),
      );
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      button.setAttribute('aria-disabled', 'true');
      try {
        const data = await cart.add([{ id: Number(this.idInput.value), quantity }]);
        cart.render(data.sections);
        announce(format(config.strings.added, { count: quantity }));
        document.querySelector('cart-drawer')?.open(button);
      } catch (error) {
        announce(error.message);
        this.showError(error.message);
      } finally {
        button.removeAttribute('aria-disabled');
      }
    }

    showError(message) {
      let el = this.form.querySelector('.form-error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'form-error text-sm';
        el.setAttribute('role', 'alert');
        this.form.append(el);
      }
      el.textContent = message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Live total on product forms: "12 x $4.99 = $59.88" ---------- */

  const money = (cents) =>
    new Intl.NumberFormat(config.money?.locale || 'en', { style: 'currency', currency: config.money?.currency || 'USD' }).format(
      cents / 100,
    );

  const updateTotal = (form) => {
    const out = form.querySelector('[data-live-total]');
    if (!out) return;
    const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
    const price = Number(out.dataset.price);
    // The total line only adds information above one piece.
    out.hidden = quantity <= 1;
    out.textContent = format(config.strings.total, {
      quantity,
      price: money(price),
      total: money(price * quantity),
    });
  };

  document.addEventListener('change', (event) => {
    const form = event.target.closest('form');
    if (form?.querySelector('[data-live-total]')) updateTotal(form);
  });

  document.addEventListener('unit:change', (event) => {
    const form = document.getElementById(event.target.dataset.formId);
    const out = form?.querySelector('[data-live-total]');
    if (!out) return;
    out.dataset.price = event.detail.price;
    updateTotal(form);
    const shown = document.querySelector('[data-product-price]');
    if (shown && event.detail.units) shown.textContent = money(Math.round(event.detail.price / event.detail.units));
  });

  /* ---------- Unit selector (product page) ---------- */

  class UnitSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const radio = event.target.closest('input[type="radio"]');
        if (!radio) return;
        const form = document.getElementById(this.dataset.formId);
        const idInput = form?.querySelector('[data-variant-input]');
        if (idInput) idInput.value = radio.value;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', radio.value);
        window.history.replaceState({}, '', url);
        this.dispatchEvent(
          new CustomEvent('unit:change', {
            bubbles: true,
            detail: {
              variantId: radio.value,
              units: Number(radio.dataset.units),
              price: Number(radio.dataset.price),
              label: radio.dataset.label,
            },
          }),
        );
      });
    }
  }
  customElements.define('unit-selector', UnitSelector);

  /* ---------- Variant picker: Size and Color (product page) ---------- */

  // The values a shopper picked on purpose, per product, so a size that lacks
  // their color does not lose it for the next size (the picker is re-rendered).
  const pickerPrefs = new Map();

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-picker-variants]').textContent);
      this.groups = [...this.querySelectorAll('.picker__group')];
      this.markAvailability();
      this.addEventListener('change', (event) => {
        if (event.target.matches('.picker__input')) this.onChange(event.target);
      });
    }

    selected() {
      return this.groups.map((g) => g.querySelector('.picker__input:checked')?.value);
    }

    // Every shown value exists (the snippet lists only real combinations); mark the
    // ones that are sold out with the choices above them.
    markAvailability() {
      const chosen = this.selected();
      this.groups.forEach((group, i) => {
        group.querySelectorAll('.picker__input').forEach((input) => {
          const ok = this.variants.some(
            (v) => v.available && v.options[i] === input.value && v.options.slice(0, i).every((o, k) => o === chosen[k]),
          );
          group.querySelector(`label[for="${input.id}"]`)?.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    // Size is the root: keep the choices up to the one just changed, then for each
    // later option keep the current value when it exists, else take the first
    // value (in stock first) that the earlier choices have.
    resolve(changedIndex) {
      const want = this.selected();
      let pool = this.variants.filter((v) => v.options.slice(0, changedIndex + 1).every((o, k) => o === want[k]));
      for (let k = changedIndex + 1; k < want.length && pool.length; k++) {
        const liked = pickerPrefs.get(this.dataset.url)?.[k];
        const same = pool.filter((v) => v.options[k] === want[k]);
        const back = pool.filter((v) => v.options[k] === liked);
        if (back.length) {
          pool = back;
        } else if (same.length) {
          pool = same;
        } else {
          const pick = (pool.find((v) => v.available) || pool[0]).options[k];
          pool = pool.filter((v) => v.options[k] === pick);
        }
      }
      return pool.find((v) => v.available) || pool[0];
    }

    async onChange(input) {
      const index = this.groups.findIndex((g) => g.contains(input));
      const prefs = pickerPrefs.get(this.dataset.url) || {};
      prefs[index] = input.value;
      pickerPrefs.set(this.dataset.url, prefs);
      const variant = this.resolve(index);
      if (!variant) return;
      const focusName = input.name;
      const focusValue = input.value;
      // Keep the page's own parameters (theme previews use preview_theme_id; without
      // it Shopify renders the section with the live theme and nothing updates).
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.pathname + url.search);
      const form = document.getElementById(this.dataset.formId);
      const idInput = form?.querySelector('[data-variant-input]');
      if (idInput) idInput.value = variant.id;
      this.classList.add('is-loading');
      try {
        url.searchParams.set('section_id', this.dataset.sectionId);
        const html = await fetch(url).then((r) => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        const id = `shopify-section-${this.dataset.sectionId}`;
        const next = fresh.getElementById(id);
        const now = document.getElementById(id);
        if (!next || !now) throw new Error('section not rendered');
        now.innerHTML = next.innerHTML;
        now.querySelector(`input[name="${CSS.escape(focusName)}"][value="${CSS.escape(focusValue)}"]`)?.focus();
      } catch (error) {
        // Fall back to a normal page load of the chosen variant.
        url.searchParams.delete('section_id');
        window.location.href = url.pathname + url.search;
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Hero video: pause button, reduced motion ---------- */

  class HeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-toggle]');
      if (!this.video || !this.button) return;
      this.button.addEventListener('click', () => (this.video.paused ? this.play() : this.pause()));
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.pause();
    }

    play() {
      this.video.play();
      this.sync(false);
    }

    pause() {
      this.video.pause();
      this.sync(true);
    }

    sync(paused) {
      this.button.setAttribute('aria-pressed', String(paused));
      this.button.querySelector('[data-label-pause]').hidden = paused;
      this.button.querySelector('[data-label-play]').hidden = !paused;
    }
  }
  customElements.define('hero-video', HeroVideo);

  /* ---------- Cart controls shared by the drawer and the cart page ---------- */

  class CartBase extends HTMLElement {
    bindControls() {
      this.addEventListener('change', debounce((event) => this.onQuantity(event), 300));
      this.addEventListener('click', async (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (remove) {
          await this.update(() => cart.change(Number(remove.dataset.removeLine), 0));
          return;
        }
        const toCase = event.target.closest('[data-switch-to-case]');
        if (toCase) {
          await this.update(async () => {
            await cart.change(Number(toCase.dataset.line), 0);
            return cart.add([{ id: Number(toCase.dataset.caseId), quantity: 1 }]);
          });
        }
      });
    }

    async onQuantity(event) {
      const input = event.target.closest('input[data-line]');
      if (!input) return;
      await this.update(() => cart.change(Number(input.dataset.line), Number(input.value)));
    }

    // Remember which control had focus (line key + control), so focus can return
    // to the same control after the re-render.
    focusTarget() {
      const el = document.activeElement;
      const line = el?.closest('[data-line-key]');
      if (!line || !this.contains(el)) return null;
      let control = 'input';
      if (el.matches('[data-step]')) control = `[data-step="${el.dataset.step}"]`;
      else if (el.matches('[data-remove-line]')) control = '[data-remove-line]';
      else if (el.matches('[data-switch-to-case]')) control = '[data-switch-to-case]';
      return { key: line.dataset.lineKey, control };
    }

    headingSelector() {
      return 'h1, h2';
    }

    async update(action) {
      const remembered = this.focusTarget();
      try {
        const data = await action();
        cart.render(data.sections);
        if (!data.sections) {
          const ids = [cart.sectionId, ...cart.extraSections].join(',');
          const res = await fetch(`${window.location.pathname}?sections=${ids}`);
          cart.render(await res.json());
        }
        announce(this.querySelector(this.headingSelector())?.textContent.trim() || '');
      } catch (error) {
        announce(error.message);
      }
      // Back to the same control on the same line, or the heading if the line is gone.
      const line = remembered && this.querySelector(`[data-line-key="${CSS.escape(remembered.key)}"]`);
      const target = line?.querySelector(remembered.control) || this.querySelector(this.headingSelector());
      if (target) {
        if (!target.hasAttribute('tabindex') && !target.matches('input, button, a')) target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }
  }

  /* ---------- Cart drawer ---------- */

  class CartDrawer extends CartBase {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-open-cart]');
        if (!trigger) return;
        // On the cart page the icon simply stays a link to the page.
        if (document.querySelector('cart-page')) return;
        event.preventDefault();
        this.open(trigger);
      });
      this.bindControls();
    }

    headingSelector() {
      return '#CartDrawerTitle';
    }

    open(opener) {
      openDialog(this.dialog, opener);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Cart page ---------- */

  class CartPage extends CartBase {
    connectedCallback() {
      cart.extraSections = [this.dataset.sectionId];
      document.addEventListener('cart:sections', (event) => {
        const html = event.detail[this.dataset.sectionId];
        if (!html) return;
        const next = new DOMParser().parseFromString(html, 'text/html').getElementById('CartPageContent');
        const current = this.querySelector('#CartPageContent');
        if (next && current) current.replaceWith(next);
      });
      this.bindControls();
    }

    headingSelector() {
      return 'h1';
    }
  }
  customElements.define('cart-page', CartPage);

  /* ---------- Event details (spec 1.5) ---------- */

  class EventDetails extends HTMLElement {
    connectedCallback() {
      this.fields = this.querySelector('[data-event-fields]');
      this.date = this.querySelector('input[name="Event date"]');
      this.noDate = this.querySelector('input[name="No event date"]');
      this.status = this.querySelector('[data-event-status]');
      this.warning = this.querySelector('[data-event-warning]');
      this.summary = this.querySelector('[data-event-summary]');
      this.save = debounce(() => this.persist(), 500);
      this.fields.addEventListener('input', () => this.save());
      this.fields.addEventListener('change', (event) => {
        if (event.target === this.noDate) {
          this.date.disabled = this.noDate.checked;
          if (this.noDate.checked) this.date.value = '';
        }
        this.save();
        this.checkWarning();
      });
      this.onEstimate = (event) => {
        this.latest = event.detail.latest;
        this.checkWarning();
      };
      document.addEventListener('delivery:estimate', this.onEstimate);
      this.onSaved = (event) => {
        if (event.detail.source !== this) this.apply(event.detail.values);
      };
      document.addEventListener('event:saved', this.onSaved);
    }

    disconnectedCallback() {
      document.removeEventListener('delivery:estimate', this.onEstimate);
      document.removeEventListener('event:saved', this.onSaved);
    }

    values() {
      const buyer = this.querySelector('input[name="Buyer type"]:checked');
      return {
        attributes: {
          'Event date': this.noDate.checked ? '' : this.date.value,
          'No event date': this.noDate.checked ? 'Yes' : '',
          'Buyer type': buyer ? buyer.value : '',
          'PO number': this.querySelector('input[name="PO number"]').value.trim(),
        },
        note: this.querySelector('textarea[name="note"]').value,
      };
    }

    apply(values) {
      this.date.value = values.attributes['Event date'];
      this.noDate.checked = values.attributes['No event date'] === 'Yes';
      this.date.disabled = this.noDate.checked;
      this.querySelectorAll('input[name="Buyer type"]').forEach((r) => (r.checked = r.value === values.attributes['Buyer type']));
      this.querySelector('input[name="PO number"]').value = values.attributes['PO number'];
      this.querySelector('textarea[name="note"]').value = values.note;
      this.updateSummary(values);
      this.checkWarning();
    }

    updateSummary(values) {
      const a = values.attributes;
      const parts = [];
      if (a['Event date']) {
        const D = window.BotaniqueDelivery;
        parts.push(D ? D.formatDay(a['Event date']) : a['Event date']);
      } else if (a['No event date']) parts.push(this.noDate.parentElement.textContent.trim());
      if (a['Buyer type']) parts.push(a['Buyer type']);
      if (a['PO number']) parts.push(/^po/i.test(a['PO number']) ? a['PO number'] : `PO ${a['PO number']}`);
      if (parts.length) this.summary.textContent = parts.join(', ');
    }

    async persist() {
      const values = this.values();
      const previousDate = this.lastDate;
      try {
        const res = await fetch(config.routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(values),
        });
        if (!res.ok) throw new Error();
        this.status.textContent = config.strings.eventSaved;
        this.updateSummary(values);
        document.dispatchEvent(new CustomEvent('event:saved', { detail: { values, source: this } }));
        const date = values.attributes['Event date'];
        if (date && date !== previousDate) publish('botanique:event_date_set', { event_date: date });
        this.lastDate = date;
      } catch (e) {
        this.status.textContent = config.strings.addError;
      }
    }

    checkWarning() {
      const D = window.BotaniqueDelivery;
      const event = !this.noDate.checked && this.date.value;
      if (!D || !event || !this.latest) {
        this.warning.hidden = true;
        return;
      }
      const gap = D.businessDaysBetween(this.latest, event);
      if (gap > 2) {
        this.warning.hidden = true;
        return;
      }
      const s = config.strings;
      this.warning.replaceChildren();
      const p = document.createElement('p');
      p.textContent = format(s.eventWarning, { event: D.formatDay(event), latest: D.formatDay(this.latest) });
      this.warning.append(p);
      if (config.routes.quote) {
        const a = document.createElement('a');
        a.href = `${config.routes.quote}?source=cart&rush=1`;
        a.textContent = s.eventRush;
        this.warning.append(a);
      }
      if (this.dataset.phone && !this.dataset.phone.includes('[TBD]')) {
        this.warning.append(document.createTextNode(` ${format(s.eventCall, { phone: this.dataset.phone })}`));
      }
      this.warning.hidden = false;
    }
  }
  customElements.define('event-details', EventDetails);

  /* ---------- Delivery promise (spec 7) ---------- */

  const ZIP_KEY = 'botanique:zip';
  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* private mode: the ZIP is simply not remembered */
      }
    },
  };
  window.Botanique.storage = storage;

  class DeliveryPromise extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.dataset.config);
      } catch (e) {
        return;
      }
      this.line = this.querySelector('.delivery__line');
      this.form = this.querySelector('form');
      this.input = this.form.querySelector('input');
      this.error = this.form.querySelector('.delivery__error');
      this.change = this.querySelector('[data-change-zip]');
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.setZip(this.input.value);
      });
      this.change.addEventListener('click', () => {
        this.form.hidden = false;
        this.change.hidden = true;
        this.input.focus();
      });
      document.addEventListener('delivery:zip', (event) => {
        if (event.detail.source !== this) this.render(event.detail.zip, false);
      });
      const saved = storage.get(ZIP_KEY);
      if (saved) this.render(saved, false);
      else this.form.hidden = false;
    }

    setZip(value) {
      const D = window.BotaniqueDelivery;
      if (!D || !D.stateForZip(value)) {
        this.error.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
        return;
      }
      this.error.hidden = true;
      this.input.removeAttribute('aria-invalid');
      storage.set(ZIP_KEY, value.trim());
      this.render(value.trim(), true);
      document.dispatchEvent(new CustomEvent('delivery:zip', { detail: { zip: value.trim(), source: this } }));
    }

    render(zip, announceIt) {
      const D = window.BotaniqueDelivery;
      if (!D) return;
      const state = D.stateForZip(zip);
      const c = this.config;
      const s = config.strings;
      let text;
      if (state === 'OTHER') {
        text = s.deliveryOutside;
      } else if (!c.rules[state]) {
        text = format(s.deliveryNoRule, { state });
      } else {
        const r = D.estimate({
          now: new Date(),
          cutoffHour: c.cutoff,
          processingDays: c.processing,
          leadDays: c.lead,
          transit: c.rules[state],
          holidays: c.holidays,
        });
        const values = {
          cutoff: D.formatHour(c.cutoff),
          day: D.formatDay(r.orderDay),
          earliest: D.formatDay(r.earliest),
          latest: D.formatDay(r.latest),
          state,
        };
        const template = r.cutoffToday ? s.deliveryToday : s.deliveryLater;
        text = r.earliest === r.latest ? `${format(template.split(',')[0], values)}, ${format(s.deliverySameDay, values)}` : format(template, values);
        this.result = { state, ...r };
        publish('botanique:delivery_estimate_viewed', { state, earliest: r.earliest, latest: r.latest });
        document.dispatchEvent(new CustomEvent('delivery:estimate', { detail: this.result }));
      }
      this.line.textContent = text;
      this.form.hidden = true;
      this.change.hidden = false;
      this.input.value = zip;
      if (announceIt) announce(text);
    }
  }
  customElements.define('delivery-promise', DeliveryPromise);

  /* ---------- Product gallery: swipe, arrows, keys, thumbs, zoom ---------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.gallery__track');
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.status = this.querySelector('[data-gallery-status]');
      this.dialog = this.querySelector('dialog');
      this.index = 0;

      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.galleryThumb))));
      this.querySelector('.gallery__viewport').addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
      this.querySelector('[data-gallery-zoom]').addEventListener('click', (event) => this.open(this.index, event.currentTarget));
      this.querySelectorAll('[data-gallery-open]').forEach((b) =>
        b.addEventListener('click', (event) => this.open(Number(b.dataset.galleryOpen), event.currentTarget)),
      );

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setCurrent(Number(entry.target.dataset.index), false);
          });
        },
        { root: this.track, threshold: [0.6] },
      );
      this.slides.forEach((s) => observer.observe(s));
    }

    go(i) {
      const next = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.setCurrent(next, true);
    }

    setCurrent(i, announceIt) {
      if (i === this.index && !announceIt) return;
      this.index = i;
      this.thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      if (announceIt && this.status) this.status.textContent = `${i + 1} / ${this.slides.length}`;
    }

    /* Viewer: built on first open from the slides, so the page carries no extra images. */
    buildViewer() {
      if (this.viewer) return;
      const track = this.dialog.querySelector('[data-lightbox-track]');
      const thumbs = this.dialog.querySelector('[data-lightbox-thumbs]');
      this.viewer = { track, frames: [], thumbs: [] };
      this.slides.forEach((slide, i) => {
        const li = document.createElement('li');
        li.className = 'lightbox__slide';
        li.dataset.index = i;
        const frame = document.createElement('div');
        frame.className = `lightbox__frame${slide.classList.contains('gallery__slide--studio') ? ' lightbox__frame--studio' : ''}`;
        const img = document.createElement('img');
        // Start with the image the page already loaded, then swap in the sharp one.
        const shown = slide.querySelector('img');
        img.src = shown?.currentSrc || slide.dataset.zoomSrc;
        img.alt = shown?.alt || '';
        img.decoding = 'async';
        if (img.src !== slide.dataset.zoomSrc) {
          const sharp = new Image();
          sharp.onload = () => (img.src = sharp.src);
          frame.addEventListener('pointerenter', () => (sharp.src ||= slide.dataset.zoomSrc), { once: true });
          li.dataset.sharp = slide.dataset.zoomSrc;
          this.viewer.loaders ??= [];
          this.viewer.loaders[i] = sharp;
        }
        frame.append(img);
        frame.addEventListener('click', (event) => this.toggleZoom(frame, event));
        frame.addEventListener('pointermove', (event) => frame.classList.contains('is-zoomed') && this.panZoom(frame, event));
        li.append(frame);
        track.append(li);
        this.viewer.frames.push(frame);
        if (thumbs) {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', format(window.Botanique?.strings?.imageCount || '[n] / [total]', { n: i + 1, total: this.slides.length }));
          const t = document.createElement('img');
          t.src = slide.querySelector('img')?.currentSrc || slide.dataset.zoomSrc;
          t.alt = '';
          b.append(t);
          b.addEventListener('click', () => this.show(i));
          const tli = document.createElement('li');
          tli.append(b);
          thumbs.append(tli);
          this.viewer.thumbs.push(b);
        }
      });
      this.dialog.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.show(this.viewIndex - 1));
      this.dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.show(this.viewIndex + 1));
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.show(this.viewIndex + 1);
        if (event.key === 'ArrowLeft') this.show(this.viewIndex - 1);
      });
      // Swipes and trackpad scrolls update the counter once a slide settles.
      const seen = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.isIntersecting && this.mark(Number(e.target.dataset.index))),
        { root: track, threshold: 0.6 },
      );
      track.querySelectorAll('.lightbox__slide').forEach((li) => seen.observe(li));
      this.dialog.addEventListener('close', () => {
        this.viewer.frames.forEach((f) => f.classList.remove('is-zoomed'));
        // Keep the page gallery on the image the shopper ended on.
        if (this.viewIndex !== this.index) this.go(this.viewIndex);
      });
    }

    open(i, opener) {
      this.buildViewer();
      openDialog(this.dialog, opener);
      this.show(i, true);
    }

    show(i, instant) {
      const n = this.slides.length;
      const next = (i + n) % n;
      const li = this.viewer.track.children[next];
      this.viewer.frames.forEach((f) => f.classList.remove('is-zoomed'));
      // Load the sharp image for this slide and its neighbours.
      [next, (next + 1) % n, (next - 1 + n) % n].forEach((k) => {
        const l = this.viewer.loaders?.[k];
        if (l && !l.src) l.src = this.viewer.track.children[k].dataset.sharp;
      });
      this.viewer.track.scrollTo({ left: li.offsetLeft, behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
      this.mark(next);
    }

    mark(i) {
      this.viewIndex = i;
      const count = this.dialog.querySelector('[data-lightbox-count]');
      if (count) count.textContent = format(this.dialog.dataset.countLabel || '[n] / [total]', { n: i + 1, total: this.slides.length });
      this.viewer.thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      this.viewer.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    toggleZoom(frame, event) {
      this.panZoom(frame, event);
      frame.classList.toggle('is-zoomed');
    }

    panZoom(frame, event) {
      const r = frame.getBoundingClientRect();
      frame.style.setProperty('--zoom-x', `${(((event.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
      frame.style.setProperty('--zoom-y', `${(((event.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Mobile sticky add bar ---------- */

  class StickyAdd extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.formId);
      const main = form?.querySelector('[data-main-add]');
      if (!main) return;
      const unit = this.querySelector('[data-sticky-unit]');
      const total = this.querySelector('[data-sticky-total]');
      const sync = () => {
        const out = form.querySelector('[data-live-total]');
        const qty = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
        if (out) total.textContent = money(Number(out.dataset.price) * qty);
        const checked = document.querySelector(`unit-selector[data-form-id="${this.dataset.formId}"] input:checked`);
        if (checked) unit.textContent = qty > 1 ? `${qty} x ${checked.dataset.label}` : checked.dataset.label;
      };
      form.addEventListener('change', sync);
      document.addEventListener('unit:change', () => requestAnimationFrame(sync));
      // Show only once the main button is above the viewport. A scroll check (not an
      // IntersectionObserver) because a fast scroll can jump the button from below the
      // fold to above it without ever intersecting.
      let ticking = false;
      const update = () => {
        ticking = false;
        const show = main.getBoundingClientRect().bottom < 0;
        if (show === !this.hidden) return;
        this.hidden = !show;
        document.documentElement.style.setProperty('--sticky-bar-height', show ? `${this.offsetHeight}px` : '0px');
      };
      window.addEventListener(
        'scroll',
        () => {
          if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
          }
        },
        { passive: true },
      );
      update();
    }
  }
  customElements.define('sticky-add', StickyAdd);

  /* ---------- Recently viewed (this browser only) ---------- */

  const RECENT_KEY = 'botanique:recent';

  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const current = this.dataset.handle;
      let list = [];
      try {
        list = JSON.parse(storage.get(RECENT_KEY) || '[]');
      } catch (e) {
        list = [];
      }
      const others = list.filter((h) => h !== current).slice(0, 8);
      storage.set(RECENT_KEY, JSON.stringify([current, ...others].slice(0, 12)));
      if (!others.length) return;
      const cards = await Promise.all(
        others.map((h) =>
          fetch(`${config.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(h)}?section_id=product-card-section`)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => ''),
        ),
      );
      const items = cards
        .map((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('.card'))
        .filter(Boolean);
      if (!items.length) return;
      const id = 'RecentlyViewedHeading';
      this.innerHTML = `<section class="product-row" aria-labelledby="${id}"><h2 class="h3 product-row__heading" id="${id}"></h2><ul class="product-row__list" role="list"></ul></section>`;
      this.querySelector('h2').textContent = this.dataset.heading;
      const ul = this.querySelector('ul');
      items.forEach((card) => {
        const li = document.createElement('li');
        li.append(card);
        ul.append(li);
      });
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- Collection and search filters (spec 4.4) ---------- */

  const VIEW_KEY = 'botanique:view';

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-sort]') || event.target.closest('[data-facets-form]')) {
          if (event.target.type === 'number') return this.debouncedRefresh();
          this.refresh();
        }
      });
      this.addEventListener('input', (event) => {
        if (event.target.type === 'number' && event.target.closest('[data-facets-form]')) this.debouncedRefresh();
      });
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-facets-form]')) return;
        event.preventDefault();
        this.refresh();
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link], .pagination a, [data-facets-clear]');
        if (link) {
          event.preventDefault();
          this.render(link.href, !link.closest('.pagination'));
          if (link.closest('.pagination')) this.scrollIntoView({ behavior: window.Botanique.scrollBehavior() });
          return;
        }
        const viewBtn = event.target.closest('[data-view]');
        if (viewBtn) this.setView(viewBtn.dataset.view, true);
      });
      window.addEventListener('popstate', () => this.render(window.location.href, false, true));
      this.debouncedRefresh = debounce(() => this.refresh(), 600);
      this.setView(storage.get(VIEW_KEY) || 'grid', false);
    }

    url() {
      const form = this.querySelector('[data-facets-form]');
      const params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
      const sort = this.querySelector('[data-sort]');
      if (sort && !form) params.set('sort_by', sort.value);
      // Drop empty price fields so the URL stays clean.
      for (const [key, value] of [...params.entries()]) if (value === '') params.delete(key);
      const base = this.dataset.baseUrl;
      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    refresh() {
      this.render(this.url(), true);
    }

    async render(url, push, fromHistory) {
      const target = new URL(url, window.location.origin);
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      this.classList.add('is-loading');
      const focusId = document.activeElement?.id;
      try {
        const html = await fetch(fetchUrl).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Read before swapping: replaceWith moves nodes out of the fetched document.
        const count = doc.querySelector('[data-results-count]')?.dataset.resultsCount || '0';
        const swap = (selector) => {
          const next = doc.querySelector(selector);
          const current = this.querySelector(selector);
          if (next && current) current.replaceWith(next);
        };
        const dialog = this.querySelector('dialog.facets');
        if (dialog?.open) {
          swap('.facets__body');
          swap('[data-show-results]');
          swap('[data-facets-clear]');
        } else {
          swap('.results__rail');
        }
        swap('[data-chips]');
        swap('.results__main');
        if (!fromHistory && push) window.history.pushState({}, '', target.pathname + target.search);
        this.setView(storage.get(VIEW_KEY) || 'grid', false);
        announce(format(config.strings.filterResults, { count }));
        publish('botanique:filters_applied', { url: target.pathname + target.search, count: Number(count) });
      } catch (e) {
        window.location.href = target.href;
        return;
      } finally {
        this.classList.remove('is-loading');
      }
      const again = focusId && document.getElementById(focusId);
      if (again) again.focus();
    }

    setView(view, save) {
      const grid = this.querySelector('#ResultsGrid.product-grid');
      grid?.classList.toggle('is-list', view === 'list');
      this.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
      if (save) storage.set(VIEW_KEY, view);
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- SKU index (quick order, lists, quote), cached per session ---------- */

  const INDEX_KEY = 'botanique:sku-index';
  let indexPromise = null;
  window.Botanique.loadSkuIndex = () => {
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
      try {
        const cached = sessionStorage.getItem(INDEX_KEY);
        if (cached) return JSON.parse(cached);
      } catch (e) {
        /* fall through to fetch */
      }
      const items = [];
      let page = 1;
      let pages = 1;
      const base = `${config.routes.allProducts}?view=sku-index`;
      do {
        const data = await fetch(`${base}&page=${page}`).then((r) => r.json());
        items.push(...data.items);
        pages = data.pages;
        page += 1;
      } while (page <= pages);
      const index = items.filter((i) => i.sku);
      try {
        sessionStorage.setItem(INDEX_KEY, JSON.stringify(index));
      } catch (e) {
        /* storage full or blocked: memory only */
      }
      return index;
    })();
    indexPromise.catch(() => (indexPromise = null));
    return indexPromise;
  };

  /* ---------- Saved event lists: storage and share links (spec 6.2) ---------- */

  const LISTS_KEY = 'botanique:lists';
  const b64url = {
    encode: (text) =>
      btoa(String.fromCharCode(...new TextEncoder().encode(text)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, ''),
    decode: (code) => {
      const bin = atob(code.replace(/-/g, '+').replace(/_/g, '/'));
      return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
    },
  };

  const lists = {
    all() {
      try {
        const data = JSON.parse(storage.get(LISTS_KEY) || '[]');
        return Array.isArray(data) ? data : [];
      } catch (e) {
        return [];
      }
    },
    write(all) {
      storage.set(LISTS_KEY, JSON.stringify(all));
      document.dispatchEvent(new CustomEvent('lists:changed'));
    },
    get(id) {
      return this.all().find((l) => l.id === id) || null;
    },
    create(name, date, items) {
      const list = { id: `l${Date.now().toString(36)}`, name: name || '', date: date || '', items: items || [], updated: Date.now() };
      this.write([list, ...this.all()]);
      publish('botanique:list_saved', { items: list.items.length });
      return list;
    },
    update(id, changes) {
      const all = this.all().map((l) => (l.id === id ? { ...l, ...changes, updated: Date.now() } : l));
      this.write(all);
    },
    remove(id) {
      this.write(this.all().filter((l) => l.id !== id));
    },
    addItem(id, item) {
      const list = this.get(id);
      if (!list) return;
      const items = [...list.items];
      const existing = items.find((i) => i.v === item.v);
      if (existing) existing.q += item.q;
      else items.push(item);
      this.update(id, { items });
      publish('botanique:list_saved', { items: items.length });
    },
    // Share payload: { n: name, d: date, i: [[variantId, qty], ...] }
    encode(list) {
      return b64url.encode(JSON.stringify({ n: list.name, d: list.date, i: list.items.map((x) => [x.v, x.q]) }));
    },
    decode(code) {
      const data = JSON.parse(b64url.decode(code));
      return {
        name: String(data.n || ''),
        date: String(data.d || ''),
        items: (data.i || []).map(([v, q]) => ({ v: Number(v), q: Math.max(1, Number(q) || 1) })).filter((x) => x.v),
      };
    },
    shareUrl(list) {
      const path = config.routes.lists || '/pages/lists';
      return new URL(`${path}?l=${this.encode(list)}`, window.location.origin).href;
    },
  };
  window.Botanique.lists = lists;

  /* ---------- Save to list dialog (product page, cart page) ---------- */

  class SaveToList extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.choices = this.querySelector('[data-list-choices]');
      this.querySelector('[data-save-open]').addEventListener('click', (event) => this.open(event.currentTarget));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.save();
      });
    }

    items() {
      if (this.dataset.mode === 'cart') return Promise.resolve(null);
      const form = document.getElementById(this.dataset.formId);
      const id = Number(form?.querySelector('[data-variant-input]')?.value);
      const q = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value) || 1);
      return Promise.resolve(id ? [{ v: id, q }] : []);
    }

    open(opener) {
      const all = lists.all();
      this.choices.replaceChildren();
      const cartMode = this.dataset.mode === 'cart';
      if (!cartMode) {
        all.forEach((l, i) => {
          const label = document.createElement('label');
          label.className = 'save-list__choice';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'list';
          input.value = l.id;
          input.checked = i === 0;
          label.append(input, document.createTextNode(` ${l.name || config.strings.listUntitled} (${l.items.length})`));
          this.choices.append(label);
        });
      }
      const label = document.createElement('label');
      label.className = 'save-list__choice';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'list';
      input.value = 'new';
      input.checked = cartMode || !all.length;
      label.append(input, document.createTextNode(` ${config.strings.listNew}`));
      this.choices.append(label);
      this.choices.hidden = cartMode;
      openDialog(this.dialog, opener);
    }

    async save() {
      const data = new FormData(this.form);
      const choice = data.get('list') || 'new';
      let items = await this.items();
      if (items === null) {
        const c = await fetch(`${config.routes.cart}.js`).then((r) => r.json());
        items = c.items.map((i) => ({ v: i.variant_id, q: i.quantity }));
      }
      if (!items.length) return;
      let list;
      if (choice === 'new') {
        list = lists.create(String(data.get('name') || '').trim(), String(data.get('date') || ''), items);
      } else {
        items.forEach((item) => lists.addItem(choice, item));
        list = lists.get(choice);
      }
      this.dialog.close();
      const message = format(config.strings.listSaved, { list: list.name || config.strings.listUntitled });
      announce(message);
      const status = this.querySelector('[data-save-status]');
      if (status) {
        status.replaceChildren(document.createTextNode(`${message} `));
        if (config.routes.lists) {
          const a = document.createElement('a');
          a.href = config.routes.lists;
          a.textContent = config.strings.listView;
          status.append(a);
        }
      }
      this.form.reset();
    }
  }
  customElements.define('save-to-list', SaveToList);

  // Respect reduced motion for any smooth scrolling triggered from script.
  window.Botanique.scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
})();
